import React, { ReactNode, useState, ComponentProps } from 'react';
import styled from 'styled-components';

import Button from 'components/basic/Button/Button';
import useEventCallback from 'hooks/useEventCallback';
import { makeEthersErrorHumanRedable } from 'lib/ethereum/ethersErrors';

import ErrorPanel from '../panels/ErrorPanel';

import ModalFooterButtons from './components/ModalFooterButtons';
import SimpleModalOrMobilePage from './SimpleModalOrMobilePage';

const ChildrenWrap = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  text-align: left;
  /* text-align: center; */
  /* Wrap long texts in middle - eg. wallet address */
  overflow-wrap: break-word;
  font-size: var(--medium2);
  /* align-items: center; */
  padding: 32px 24px;
`;

// const ButtonsWrap = styled.div`
//   max-width: 100%;
//   display: flex;
//   justify-content: center;
//   /* border-top: 1px solid var(--gray1); */
//   margin-top: 14px;
//   gap: 10px;
//   width: 100%;
//   .isDesktopStyle & {
//     padding: 16px 0;
//     border-top: 1px solid var(--separator);
//     display: flex;
//     justify-content: flex-end;
//   }
// `;

const H2 = styled.h2`
  color: var(--gray4c);
  font-size: var(--small);
  line-height: normal;
  padding: 8px 0;

  .isMobileStyle & {
    text-align: center;
    color: var(--white);
  }
`;

// const H2 = styled.h2`
//   font-size: var(--medium2);
//   line-height: normal;
//   .isMobileStyle & {
//     text-align: center;
//   }
// `;

type Props = {
  title?: ReactNode;
  childrenWrapStyle?: React.CSSProperties;
  confirmText?: ReactNode;
  confirmButtonProps?: Partial<ComponentProps<typeof Button>>;
  processing: boolean;
  errorMessage?: string;
  onClose: (() => void) | undefined;
  onConfirm: () => void;
  children?: ReactNode;
  childrenBottom?: ReactNode;
};

export default function ConfirmationModal({
  title,
  childrenWrapStyle,
  confirmText = 'Ok',
  confirmButtonProps,
  processing,
  errorMessage,
  onClose,
  onConfirm,
  children,
  childrenBottom,
}: Props) {
  // const isMobile = useIsMobile();
  return (
    <SimpleModalOrMobilePage
      title={title}
      onClose={onClose}
      size="small"
      padding="0"
      verticalAlignOnMobile="ceneteredDiv"
    >
      {children && (
        <ChildrenWrap style={childrenWrapStyle}>{children}</ChildrenWrap>
      )}
      {errorMessage && <ErrorPanel marginTop={8} message={errorMessage} />}
      <ModalFooterButtons
        confirmButtonProps={{
          widePadding: true,
          ...confirmButtonProps,
          disabled: confirmButtonProps?.disabled || processing,
          onClick: onConfirm,
          children: confirmText,
        }}
      >
        {childrenBottom}
      </ModalFooterButtons>
    </SimpleModalOrMobilePage>
  );
}

type ReactNodeOrDataFn<D> = ReactNode | ((data?: D) => ReactNode);

type CallbackContext = {
  setConfirmText: React.Dispatch<React.SetStateAction<ReactNode>>;
};

type HookParams<D> = {
  title?: ReactNode;
  message?: ReactNodeOrDataFn<D>;
  children?: ReactNodeOrDataFn<D>;
  confirmText?: ReactNode;
  confirmButtonProps?: Partial<ComponentProps<typeof Button>>;
  childrenWrapStyle?: React.CSSProperties;
  onConfirm: (
    data: D | undefined,
    context: CallbackContext
  ) => unknown | Promise<unknown>;
  onReject?: (data: D | undefined) => void;
};

/**
 * Keywords: dialog, modal, showModal, makeConfirm, window.confirm
 * @example
 *
 *  const [modalPlaceholder, show] = useConfirmationModal({
 *   message: 'Will you confirm?',
 *   onConfirm: (accepted) => alert(accepted)
 *  });
 *  return <>
 *    {modalPlaceholder}
 *    <div onClick={show}>Confirm me</div>
 *  </>
 */
export function useConfirmationModal<D>({
  title,
  message,
  children,
  childrenWrapStyle,
  confirmText = 'Ok',
  confirmButtonProps,
  onConfirm,
  onReject,
}: HookParams<D>): [ReactNode, (data?: D) => void] {
  /* we use { data } so setting a fn to the showModal fn does not
     use the react state update fn instead of set the fn as the state. */
  const [{ data }, setData] = useState<{ data: undefined | D }>({
    data: undefined,
  });
  const [processing, setProcessing] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [displayed, setDisplayed] = useState(false);
  // allows changing confirm text value while awaiting a result or completion
  const [confirmTextValue, setConfirmText] = useState<ReactNode>(confirmText);

  const handleConfirm = useEventCallback(async (data?: D) =>
    onConfirm(data, {
      setConfirmText,
    })
  );

  const handleShowModal = useEventCallback((clientData?: D) => {
    if (clientData) {
      setData({ data: clientData });
    }
    setDisplayed(true);
  });

  const handleReject = useEventCallback(() => {
    setProcessing(false);
    setErrorMessage(undefined);
    setDisplayed(false);
    if (onReject) {
      onReject(data);
    }
  });

  const placeholderHtml = displayed ? (
    <ConfirmationModal
      title={title}
      childrenWrapStyle={childrenWrapStyle}
      confirmText={confirmTextValue}
      confirmButtonProps={confirmButtonProps}
      processing={processing}
      errorMessage={errorMessage}
      onConfirm={async () => {
        setProcessing(true);
        try {
          await Promise.resolve(handleConfirm(data));
          setDisplayed(false);

          setErrorMessage(undefined);
        } catch (error) {
          console.warn(error);
          setErrorMessage(makeEthersErrorHumanRedable(error));
        } finally {
          setConfirmText(confirmText);
          setProcessing(false);
        }
      }}
      onClose={processing ? undefined : handleReject}
    >
      {message && (
        <H2>{typeof message === 'function' ? message(data) : message}</H2>
      )}
      {typeof children === 'function' ? children(data) : children}
    </ConfirmationModal>
  ) : null;

  return [placeholderHtml, handleShowModal];
}
